import {Card, Segment} from 'platform/components';
import {Box, Show, VStack} from 'platform/foundation';

import {useState} from 'react';

import {GetDirectSaleResponse, GetWarehousesResponse} from '@dms/api/warehouse';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import type {EagGridMaximization} from 'features/eag-grid';

import {FullyReturnedItemsSegment} from './FullyReturnedItemsSegment';
import {RequestDetailSegment} from './RequestDetailSegment';
import {WorkItemsSegment} from './WorkItemsSegment';

enum Segments {
  WORK_ITEMS = 'workItems',
  REQUEST_DETAIL = 'requestDetail',
  FULLY_RETURNED = 'fullyReturned',
}

interface RequestTabProps extends TestIdProps {
  maximization?: EagGridMaximization;
  directSale: GetDirectSaleResponse;
  isDirectSaleLoading: boolean;
  isEditingDisabled: boolean;
  availableWarehouses: GetWarehousesResponse;
  onMaterialReturnModalOpen: VoidFunction;
}

export function RequestTab(props: RequestTabProps) {
  const [activeSegment, setActiveSegment] = useState<Segments>(Segments.WORK_ITEMS);

  const segment = (
    <Segment<Segments>
      value={activeSegment}
      onChange={setActiveSegment}
      options={[
        {
          value: Segments.WORK_ITEMS,
          label: i18n.t('entity.warehouse.labels.workItems', {
            count: props.directSale.itemsCount ?? 0,
          }),
        },
        {
          value: Segments.REQUEST_DETAIL,
          label: i18n.t('entity.warehouse.labels.requestDetail'),
        },
        {
          value: Segments.FULLY_RETURNED,
          label: i18n.t('entity.warehouse.labels.fullyReturned', {
            count: props.directSale.itemsFullyReturnedCount ?? 0,
          }),
        },
      ]}
      minWidth={28}
    />
  );

  return (
    <Card isFullHeight={activeSegment !== Segments.REQUEST_DETAIL}>
      <VStack height="100%">
        {/* Mounted on every segment: it renders the segment row with the work-item actions. */}
        <WorkItemsSegment
          segment={segment}
          isGridVisible={activeSegment === Segments.WORK_ITEMS}
          maximization={props.maximization}
          directSale={props.directSale}
          isEditingDisabled={props.isEditingDisabled}
          availableWarehouses={props.availableWarehouses}
          onMaterialReturnModalOpen={props.onMaterialReturnModalOpen}
          data-testid={suffixTestId('segments.workItems', props)}
        />

        <Show when={activeSegment === Segments.REQUEST_DETAIL}>
          <Box height="100%">
            <RequestDetailSegment
              directSale={props.directSale}
              isDirectSaleLoading={props.isDirectSaleLoading}
              isEditingDisabled={props.isEditingDisabled}
              data-testid={suffixTestId('segments.requestDetail', props)}
            />
          </Box>
        </Show>

        <Show when={activeSegment === Segments.FULLY_RETURNED}>
          <Box height="100%">
            <FullyReturnedItemsSegment
              maximization={props.maximization}
              data-testid={suffixTestId('segments.fullyReturned', props)}
            />
          </Box>
        </Show>
      </VStack>
    </Card>
  );
}
